import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import useGenericMessageSubscription from 'hooks/useGenericMessageSubscription';
import { usePrevious } from 'hooks/usePrevious';
import ArrowNarrowDownSvg from 'images/v5/icons/arrow-narrow-down.svg';
import LinkSvg from 'images/v5/icons/link-03.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import CloseSvg from 'images/v5/icons/x-close.svg';
import Avatar from 'layout/atoms/Avatar/Avatar';
import Button from 'layout/atoms/Button/Button';
import Caption from 'layout/atoms/Caption';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import Message from 'layout/atoms/Message/Message';
import PortalBackdrop from 'layout/atoms/PortalBackdrop/PortalBackdrop';
import SystemAlert from 'layout/atoms/SystemAlert';
import Tooltip from 'layout/atoms/Tooltip';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import AvatarsList from 'molecules/AvatarsList';
import MessageInput from 'organisms/MessageInput';
import { addChat, addChatMember, fetchChat, fetchMembers, removeChatMember, resetChatState, selectChat, selectChatFollowers, selectChatMembers, selectChatMessages, selectChatRefetchFlags, selectChatStatus, } from 'store/chatSlice';
import { clearUnreadCountForConversation, resetConversationUnreadCount } from 'store/inboxSlice';
import { activateSidebar, closeModal, closeSidebar, removeSidebar, selectSidebarByName, } from 'store/layoutSlice';
import { selectFolderName } from 'store/specificationsFoldersSlice';
import { selectUserProfileDataData } from 'store/userSlice';
import env from 'utils/env';
import { getEnhancedMessages } from 'utils/getEnhancedMessages';
import { getLayerZIndex } from 'utils/zIndexLayers';
import styles from './Chat.module.scss';
const SCROLL_BOTTOM_THRESHOLD = 10;
const isAtBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD;
function Chat() {
    var _a, _b;
    const { t, i18n } = useTranslation(['specification', 'common', 'chat']);
    const dispatch = useDispatch();
    const copyToClipboard = useCopyToClipboard();
    const sidebar = useSelector(selectSidebarByName('chat'));
    const chatUrl = (_a = sidebar === null || sidebar === void 0 ? void 0 : sidebar.data) === null || _a === void 0 ? void 0 : _a.id;
    const chatId = useSelector(selectChat).id;
    const pdfUrl = chatUrl.startsWith('/specifications/details')
        ? chatUrl.replace('/specifications/details/', '/specifications/pdf/').replace('/summary', '')
        : false;
    const messages = useSelector(selectChatMessages);
    const enhancedMessages = getEnhancedMessages(messages);
    const followers = useSelector(selectChatFollowers);
    const followersIds = followers === null || followers === void 0 ? void 0 : followers.map((item) => item.user);
    const allMembers = useSelector(selectChatMembers);
    const currentUser = useSelector(selectUserProfileDataData);
    const status = useSelector(selectChatStatus);
    const refetchFlags = useSelector(selectChatRefetchFlags);
    const members = useMemo(() => {
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) {
            return allMembers.filter((member) => member.id !== currentUser.id);
        }
        return allMembers;
    }, [allMembers, currentUser]);
    // Everyone left who could still be added as a follower.
    const addableMembers = useMemo(() => _.differenceBy(members, followersIds, 'id'), [members, followersIds]);
    useGenericMessageSubscription(chatId, 'chat');
    const rootFolder = useSelector((state) => state.specificationsFolders.rootFolder);
    const folderId = chatUrl.includes('/specifications/folders/')
        ? chatUrl.replace('/specifications/folders/', '')
        : rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId;
    const folderName = useSelector((state) => selectFolderName(state, folderId));
    const folderDisplayName = folderId === rootFolder.rootFolderId ? t('all_folders') : folderName;
    const specificationNameFromDetails = useSelector((state) => { var _a, _b; return (_b = (_a = state.specificationDetails) === null || _a === void 0 ? void 0 : _a.item) === null || _b === void 0 ? void 0 : _b.fancyName; });
    const specificationNameFromSidebar = (_b = sidebar === null || sidebar === void 0 ? void 0 : sidebar.data) === null || _b === void 0 ? void 0 : _b.title;
    const specificationNameFromChat = useSelector((state) => { var _a; return (_a = state.chat) === null || _a === void 0 ? void 0 : _a.title; });
    const specificationName = specificationNameFromDetails ||
        specificationNameFromSidebar ||
        specificationNameFromChat ||
        '-';
    const chatWindow = useRef(null);
    const scrollListenerCleanupRef = useRef(null);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const { opened, active, layer } = sidebar || {};
    const zIndex = getLayerZIndex(layer);
    const prevActive = usePrevious(active);
    const classes = classNames(styles.wrap, { [styles.active]: active });
    // A callback ref instead of a plain useRef + useEffect(..., [deps]): this
    // sidebar is wrapped in PortalBackdrop, which unmounts its whole subtree
    // (including <main>) whenever it's closed and remounts a brand new one on
    // reopen — with status.fetch often unchanged across that cycle (already
    // 'succeeded' from before), so no dependency-array effect would know to
    // re-attach. A callback ref fires on every attach/detach regardless of why,
    // so the scroll listener always ends up on whichever <main> is mounted now.
    const setChatWindow = useCallback((node) => {
        var _a;
        (_a = scrollListenerCleanupRef.current) === null || _a === void 0 ? void 0 : _a.call(scrollListenerCleanupRef);
        scrollListenerCleanupRef.current = null;
        chatWindow.current = node;
        if (node) {
            const handleScroll = () => setShowScrollToBottom(!isAtBottom(node));
            node.addEventListener('scroll', handleScroll);
            handleScroll();
            scrollListenerCleanupRef.current = () => node.removeEventListener('scroll', handleScroll);
        }
    }, []);
    // A new message arrived — snap to the bottom and hide the button again.
    useEffect(() => {
        if (chatWindow.current) {
            chatWindow.current.scrollTop = chatWindow.current.scrollHeight;
        }
        setShowScrollToBottom(false);
    }, [messages]);
    useEffect(() => {
        if (opened) {
            setTimeout(() => {
                dispatch(activateSidebar({ name: 'chat' }));
            }, 10);
        }
    }, [dispatch, opened]);
    useEffect(() => {
        if (prevActive && !active) {
            setTimeout(() => {
                dispatch(removeSidebar({ name: 'chat' }));
            }, 350);
        }
    }, [dispatch, active, prevActive]);
    useEffect(() => {
        if (chatWindow.current) {
            chatWindow.current.scrollTo({
                top: chatWindow.current.scrollHeight,
                behavior: 'auto',
            });
        }
        if (status.fetchMembers === 'idle') {
            dispatch(fetchMembers());
            moment.updateLocale(i18n.language, {
                relativeTime: {},
            });
        }
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchChat({ url: chatUrl }));
        }
    }, [dispatch, i18n, chatUrl, status, refetchFlags]);
    useEffect(() => {
        dispatch(addChat({
            url: chatUrl,
            title: chatUrl.startsWith('/specifications/details')
                ? specificationName
                : folderDisplayName,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, chatUrl]);
    // Clear unread count when chat loads successfully
    useEffect(() => {
        if (chatId && status.fetch === 'succeeded') {
            dispatch(clearUnreadCountForConversation({ conversationId: chatId }));
            dispatch(resetConversationUnreadCount(chatId));
        }
    }, [dispatch, chatId, status.fetch]);
    useEffect(() => {
        return () => {
            dispatch(resetChatState());
        };
    }, [dispatch]);
    const handleAddFollower = (id) => {
        dispatch(addChatMember({ conversationId: chatId, userId: id }));
    };
    const handleRemoveFollower = (id) => {
        dispatch(removeChatMember({ conversationId: chatId, userId: id }));
    };
    const scrollChatWindowToBottom = () => {
        var _a;
        (_a = chatWindow.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
            top: chatWindow.current.scrollHeight,
            behavior: 'smooth',
        });
    };
    if (status.fetch === 'idle' || status.fetch === 'loading') {
        return (React.createElement(PortalBackdrop, { opened: opened, active: active, zIndex: zIndex },
            React.createElement("div", { className: classes },
                React.createElement("header", { className: styles.header },
                    React.createElement("div", { className: styles.title },
                        React.createElement("span", null, chatUrl.startsWith('/specifications/details')
                            ? t('chat_header_specification')
                            : t('chat_header_folder')),
                        React.createElement("h3", null, chatUrl.startsWith('/specifications/details')
                            ? specificationName
                            : folderDisplayName)),
                    React.createElement(Button, { size: "s", theme: "ghost", shape: "rounded", leftIcon: React.createElement(CloseSvg, null), onClick: () => dispatch(closeSidebar({ name: 'chat' })) })),
                React.createElement("main", { className: styles.main, ref: setChatWindow },
                    React.createElement(Loader, null)))));
    }
    return (React.createElement(PortalBackdrop, { opened: opened, active: active, zIndex: zIndex },
        React.createElement("div", { className: classes },
            React.createElement("header", { className: styles.header },
                React.createElement("div", { className: styles.title },
                    React.createElement("span", null, chatUrl.startsWith('/specifications/details')
                        ? t('chat_header_specification')
                        : t('chat_header_folder')),
                    React.createElement("h3", null, chatUrl.startsWith('/specifications/details')
                        ? specificationName
                        : folderDisplayName)),
                React.createElement(Button, { theme: "ghost", shape: "rounded", leftIcon: React.createElement(CloseSvg, null), onClick: () => dispatch(closeSidebar({ name: 'chat' })) })),
            pdfUrl && (React.createElement(SystemAlert, { leftIcon: React.createElement(LinkSvg, null), aside: React.createElement(React.Fragment, null,
                    React.createElement(Button, { size: "xs", onClick: () => copyToClipboard(`${window.location.origin}${pdfUrl}`) }, t('COPY_URL_BUTTON')),
                    React.createElement(Button, { size: "xs", to: pdfUrl, onClick: () => {
                            dispatch(closeSidebar({ name: 'chat' }));
                            dispatch(closeModal({ name: 'specificationCreator' }));
                        } }, t('SHOW_PDF_BUTTON'))) }, t('SHOW_PDF_TEXT'))),
            React.createElement("main", { className: styles.main, ref: setChatWindow },
                enhancedMessages && enhancedMessages.length > 0 ? (enhancedMessages.map((message) => (React.createElement(Message, { key: message.id, id: message.id, author: message.sender.username, avatar: message.sender.avatar, text: message.text, picture: message.image, date: message.createdDate, sendByMe: message.sendByMe, showName: message.showName, showDate: message.showDate })))) : (React.createElement(EmptyState, { size: "s", noSubheader: true, header: t('chat:WRITE_FIRST_MESSAGE') })),
                status.addMessage === 'loading' && React.createElement(Loader, null)),
            React.createElement("footer", { className: styles.footer },
                React.createElement("div", { className: classNames(styles.bottomFade, { [styles.visible]: showScrollToBottom }) }),
                React.createElement("div", { className: classNames(styles.scrollToBottom, {
                        [styles.visible]: showScrollToBottom,
                    }) },
                    React.createElement("div", { className: styles.button },
                        React.createElement(Button, { theme: "border", color: "secondary", onClick: scrollChatWindowToBottom, leftIcon: React.createElement(ArrowNarrowDownSvg, null) }, t('chat:NEWEST')))),
                React.createElement(MessageInput, { conversationId: chatId, sliceType: "chat", onAfterSend: scrollChatWindowToBottom, toolbarEnd: React.createElement("div", { className: styles.followers },
                        React.createElement(AvatarsList, { avatarSize: "s" }, followers === null || followers === void 0 ? void 0 : followers.map((follower) => {
                            var _a, _b;
                            return (React.createElement(Avatar, { size: "s", key: follower.id, userName: (_a = follower === null || follower === void 0 ? void 0 : follower.user) === null || _a === void 0 ? void 0 : _a.username, src: ((_b = follower === null || follower === void 0 ? void 0 : follower.user) === null || _b === void 0 ? void 0 : _b.avatar) && `${env.static}/${follower.user.avatar}`, onClick: () => { var _a; return handleRemoveFollower((_a = follower.user) === null || _a === void 0 ? void 0 : _a.id); } }));
                        })),
                        addableMembers.length > 0 && (React.createElement(Dropdown, { position: "topRight", trigger: React.createElement(Tooltip, { delay: 1000, position: "bottom", maxWidth: 220, content: React.createElement(React.Fragment, null,
                                    React.createElement("strong", null, t('chat:ADD_FOLLOWERS')),
                                    React.createElement("p", null, t('chat:ADD_FOLLOWERS_TOOLTIP'))) },
                                React.createElement(Button, { size: "xs", color: "secondary", shape: "rounded", leftIcon: React.createElement(PlusSvg, null) })) },
                            React.createElement(Grid, { vp: 8, hp: 4, gap: 6 },
                                React.createElement(Caption, null, t('chat:ADD_FOLLOWERS')),
                                React.createElement(Grid, { gap: 0 }, addableMembers.map((member) => (React.createElement(Button, { key: member.id, align: "left", theme: "ghost", leftIcon: React.createElement(Avatar, { size: "s", src: member.avatar && `${env.static}/${member.avatar}`, userName: member.username }), onClick: () => handleAddFollower(member.id) }, member.username)))))))) })))));
}
export default Chat;
